/**
 * Modal component with portal rendering
 */
import clsx from 'clsx';
import type { ReactElement, ReactNode } from 'react';
import { createContext, useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IoClose } from 'react-icons/io5';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  /**
   * Extra classes for the content box, e.g. a larger max width on tablets. The
   * overlay is a size container, so container queries here (`@min-[700px]:`)
   * follow the space the modal actually has, also in a rotated stage.
   */
  className?: string;
  /** Rendered in the header after the title, e.g. tabs. Wraps below the title in narrow modals. */
  headerContent?: ReactNode;
}

/**
 * Element modals portal into. Defaults to `document.body`; a surface that
 * transforms its own coordinate space (the Game Companion's counter-rotated
 * stage) provides itself so its modals rotate with it.
 */
export const ModalPortalContext = createContext<HTMLElement | null>(null);

const sizeStyles = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[90vw]',
};

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  className,
  headerContent,
}: ModalProps): ReactElement | null {
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const portalContainer = useContext(ModalPortalContext);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap — focusables are re-queried on each Tab so content changes (e.g. multi-step modals) stay trapped
  useEffect(() => {
    if (!isOpen || !contentRef.current) return;
    const content = contentRef.current;

    const getFocusable = () =>
      Array.from(content.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')).filter(
        // tabIndex >= 0 skips roving-tabindex items such as inactive tabs, so the trap's ends match the real tab order
        (el) => !el.hasAttribute('disabled') && el.tabIndex >= 0 && el.getClientRects().length > 0,
      );

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      const firstElement = focusable[0];
      const lastElement = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    };

    document.addEventListener('keydown', handleTab);
    if (!content.contains(document.activeElement)) {
      const autoFocusTarget = content.querySelector<HTMLElement>('[autofocus], [data-autofocus]');
      (autoFocusTarget ?? getFocusable()[0])?.focus();
    }

    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="bg-void/80 animate-in fade-in [container-type:size] fixed inset-0 z-50 flex items-center justify-center pt-[max(1rem,env(safe-area-inset-top))] pr-[max(1rem,env(safe-area-inset-right))] pb-[max(1rem,env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))] backdrop-blur-sm duration-150"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div
        ref={contentRef}
        className={clsx(
          'bg-obsidian border-storm shadow-void/50 relative flex max-h-full w-full flex-col rounded-xl border shadow-2xl',
          'animate-in zoom-in-95 duration-150',
          sizeStyles[size],
          className,
        )}
      >
        {/* Header; compact in short viewports (phone landscape) to leave room for content */}
        {(title || showCloseButton || headerContent) && (
          <div className="border-storm flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b px-6 py-4 [@container(max-height:500px)]:py-2">
            {title && (
              <h2 id="modal-title" className="font-heading text-pearl min-w-0 flex-1 truncate text-lg font-semibold">
                {title}
              </h2>
            )}
            {headerContent && (
              <div className="[@container(max-width:520px)]:order-last [@container(max-width:520px)]:basis-full">{headerContent}</div>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="text-ash hover:text-pearl hover:bg-slate focus-ring -m-2 ml-auto rounded-lg p-3.5 transition-colors"
                aria-label="Close modal"
              >
                <IoClose className="h-5 w-5" />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="overflow-y-auto p-6 [@container(max-height:500px)]:py-4">{children}</div>
      </div>
    </div>,
    portalContainer ?? document.body,
  );
}
